"use client";

import { useId, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  axisLabel,
  cellMinterm,
  type GroupBox,
  groupSegments,
  type KmapLayout,
} from "@/lib/tool-kit/algebra/kmap";
import { downloadPng } from "@/lib/tool-kit/download";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A Karnaugh map: Gray-coded axes, the value and minterm number in every cell, and each
 * chosen group drawn as a rounded rectangle in its own colour. A group that wraps around an
 * edge is drawn as two or four pieces, one at each edge it touches. Colours are SVG
 * attributes so the PNG download matches the page.
 */
export type CellValue = "0" | "1" | "x";

export interface MapGroup {
  box: GroupBox;
  label: string;
}

const CELL_W = 64;
const CELL_H = 52;
const LEFT = 76;
const TOP = 46;
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;
const GROUP_COLORS = [0, 3, 2, 5, 4, 1, 6].map((i) => CHART_COLORS[i]);

export function groupColor(index: number): string {
  return GROUP_COLORS[index % GROUP_COLORS.length];
}

export function KmapDiagram({
  layout,
  values,
  names,
  groups,
  title,
  downloadLabel,
  fileName,
}: {
  layout: KmapLayout;
  values: CellValue[];
  names: readonly string[];
  groups: MapGroup[];
  title: string;
  downloadLabel: string;
  fileName: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const uid = useId().replace(UNSAFE_ID, "");
  const width = LEFT + layout.cols * CELL_W + 14;
  const height = TOP + layout.rows * CELL_H + 14;
  const rowNames = names.slice(0, layout.rowBits).join("");
  const colNames = names.slice(layout.rowBits, layout.n).join("");
  return (
    <div className="space-y-3">
      <svg
        aria-labelledby={`km-${uid}`}
        className="h-auto w-full max-w-lg"
        fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
        ref={ref}
        role="img"
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={`km-${uid}`}>{title}</title>
        <rect fill={CHART_INK.background} height={height} width={width} />
        <text
          fill={CHART_INK.text}
          fontSize={13}
          fontStyle="italic"
          textAnchor="end"
          x={LEFT - 8}
          y={TOP - 30}
        >
          {rowNames}
          {" ╲ "}
          {colNames}
        </text>
        {layout.colValues.map((value, c) => (
          <text
            fill={CHART_INK.muted}
            fontFamily="ui-monospace, Menlo, monospace"
            fontSize={13}
            key={`c${value}`}
            textAnchor="middle"
            x={LEFT + c * CELL_W + CELL_W / 2}
            y={TOP - 10}
          >
            {axisLabel(value, layout.colBits)}
          </text>
        ))}
        {layout.rowValues.map((value, r) => (
          <text
            dominantBaseline="middle"
            fill={CHART_INK.muted}
            fontFamily="ui-monospace, Menlo, monospace"
            fontSize={13}
            key={`r${value}`}
            textAnchor="end"
            x={LEFT - 8}
            y={TOP + r * CELL_H + CELL_H / 2}
          >
            {axisLabel(value, layout.rowBits)}
          </text>
        ))}
        {Array.from({ length: layout.rows }, (_, r) =>
          Array.from({ length: layout.cols }, (_, c) => {
            const minterm = cellMinterm(layout, r, c);
            const value = values[minterm] ?? "0";
            return (
              <g key={minterm}>
                <rect
                  fill={value === "1" ? "#eef5fb" : CHART_INK.background}
                  height={CELL_H}
                  stroke={CHART_INK.axis}
                  width={CELL_W}
                  x={LEFT + c * CELL_W}
                  y={TOP + r * CELL_H}
                />
                <text
                  dominantBaseline="middle"
                  fill={value === "0" ? CHART_INK.muted : CHART_INK.text}
                  fontSize={22}
                  fontWeight={value === "0" ? 400 : 700}
                  textAnchor="middle"
                  x={LEFT + c * CELL_W + CELL_W / 2}
                  y={TOP + r * CELL_H + CELL_H / 2 - 2}
                >
                  {value === "x" ? "X" : value}
                </text>
                <text
                  fill={CHART_INK.muted}
                  fontSize={9}
                  x={LEFT + c * CELL_W + 11}
                  y={TOP + r * CELL_H + 17}
                >
                  {minterm}
                </text>
              </g>
            );
          })
        )}
        {groups.map((group, index) =>
          groupSegments(layout, group.box).map((segment) => {
            const inset = 5 + (index % 3) * 3;
            const color = groupColor(index);
            return (
              <rect
                fill={color}
                fillOpacity={0.14}
                height={segment.rowLength * CELL_H - inset * 2}
                key={`${group.label}-${segment.row}-${segment.col}`}
                rx={12}
                stroke={color}
                strokeWidth={2.5}
                width={segment.colLength * CELL_W - inset * 2}
                x={LEFT + segment.col * CELL_W + inset}
                y={TOP + segment.row * CELL_H + inset}
              />
            );
          })
        )}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng(fileName, ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
